import { AbstractControl, AbstractControlOptions, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Moment } from 'moment';
import moment from 'moment-timezone';

import { EditorBookingItemSetting } from './editor-booking-item-setting.model';
import { EditorDataService } from '../editor-data/editor-data.service';
import { timezoneValidator } from '../editor-data/timezone-validator.directive';


const settingValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.pristine) {
    return null;
  }

  const errors = { };

  const startDateSet = control.get('startDate').value !== null;
  const startIsEmpty = !startDateSet
    && control.get('startHour').value === null
    && control.get('startMinute').value === null;

  const endDateSet = control.get('endDate').value !== null;
  const endIsEmpty = !endDateSet
    && control.get('endHour').value === null
    && control.get('endMinute').value === null;

  if (!startIsEmpty && !startDateSet) {
    errors['invalidStart'] = true;
  }

  if (!endIsEmpty && !endDateSet) {
    errors['invalidEnd'] = true;
  }

  if (startIsEmpty && endIsEmpty) {
    errors['invalidDate'] = true;
  }

  return !!errors ? errors : null;
};


export class EditorBookingItemSettingBookingDate extends EditorBookingItemSetting {
  settingClassName = 'BookingDate';
  serializedName = 'bookingDates';
  showSummary = true;

  start: Moment;
  end: Moment;
  timezone: string;

  constructor(obj?: any) {
    super({
      inheritableType: obj?.inheritableType ?? null
    });
    this.start = obj?.start ?? null;
    this.end = obj?.end ?? null;
    this.timezone = obj?.timezone ?? 'Europe/London';
  }

  createCopy(): EditorBookingItemSettingBookingDate {
    return new EditorBookingItemSettingBookingDate({
      start: this.start,
      end: this.end,
      timezone: this.timezone,
      inheritableType: this.inheritableType
    });
  }

  getTitle(): string {
    return $localize `Date`;
  }

  getHelpText(): string {
    return $localize `At which date and time does this occur?`;
  }

  hasSummary(): boolean {
    return this.start !== null || this.end !== null;
  }

  getFormConfig(editorData: EditorDataService): {
    controlsConfig: { [key: string]: any; },
    options?: AbstractControlOptions
  } {
    return {
      controlsConfig: {
        startDate: [this.start === null ? null : this.start.clone().startOf('date')],
        startHour: [this.start === null ? null : this.start.hour()],
        startMinute: [this.start === null ? null : this.start.minute()],
        endDate: [this.end === null ? null : this.end.clone().startOf('date')],
        endHour: [this.end === null ? null : this.end.hour()],
        endMinute: [this.end === null ? null : this.end.minute()],
        timezone: [this.timezone, timezoneValidator(editorData.tzNames)]
      },
      options: {
        validators: settingValidator
      }
    };
  }

  getFormValues(): any {
    return {
      startDate: this.start === null ? null : this.start.clone().startOf('date'),
      startHour: this.start === null ? null : this.start.hour(),
      startMinute: this.start === null ? null : this.start.minute(),
      endDate: this.end === null ? null : this.end.clone().startOf('date'),
      endHour: this.end === null ? null : this.end.hour(),
      endMinute: this.end === null ? null : this.end.minute(),
      timezone: this.timezone
    };
  }

  getEmptyFormValues(): any {
    return {
      startDate: null,
      startHour: null,
      startMinute: null,
      endDate: null,
      endHour: null,
      endMinute: null,
      timezone: 'Europe/London'
    };
  }

  applyFormValues(formValues: any) {
    let start: Moment = null;
    if (formValues.startDate !== null) {
      start = moment(formValues.startDate.format('YYYY-MM-DD'));
      if (formValues.startHour !== null) {
        start.hour(formValues.startHour);
      }
      if (formValues.startMinute !== null) {
        start.minute(formValues.startMinute);
      }
    }

    let end: Moment = null;
    if (formValues.endDate !== null) {
      end = moment(formValues.endDate.format('YYYY-MM-DD'));
      if (formValues.endHour !== null) {
        end.hour(formValues.endHour);
      }
      if (formValues.endMinute !== null) {
        end.minute(formValues.endMinute);
      }
    }

    this.start = start;
    this.end = end;
    this.timezone = formValues.timezone;
  }

  getSerializedValues(): any {
    // TODO: decide for a default timezone when not set
    // const timezone = !this.timezone ? moment.tz.guess() : this.timezone;
    const timezone = !this.timezone ? 'Europe/London' : this.timezone;

    const start: Moment = this.start !== null
      ? moment.tz(this.start.format('YYYY-MM-DD HH:mm'), timezone).tz('UTC')
      : this.start;

    const end: Moment = this.end !== null
      ? moment.tz(this.end.format('YYYY-MM-DD HH:mm'), timezone).tz('UTC')
      : this.end;

    return {
      startDate: start === null ? null : start.toISOString(),
      endDate: end === null ? null : end.toISOString(),
      timezone
    };
  }
}
